import React, { Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useExport } from './hooks/useExport';
import { useAppStore } from './store/useAppStore';
import { useFranchiseFinance } from './hooks/useFranchiseFinance';
import { useVersionCheck } from './hooks/useVersionCheck';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Layout & Security
import DashboardLayout from './layouts/DashboardLayout';
import ProtectedRoute from './features/auth/ProtectedRoute';
import DashboardSkeleton from './components/ui/layout/DashboardSkeleton';
import NotFound from './layouts/pages/NotFound';
import DebugFirestore from './components/debug/DebugFirestore';
import { ErrorBoundaryWithToast } from './components/error/ErrorBoundary';
import InstallPrompt from './components/pwa/InstallPrompt';

// Route modules
import { AdminRoutes } from './routes/adminRoutes';
import { RiderRoutes } from './routes/riderRoutes';
import { SharedRoutes } from './routes/sharedRoutes';

const Login = lazyWithRetry(() => import('./features/auth/Login'));

function App() {
    const { user, loading: authLoading, roleConfig, logout, isAdmin, impersonatedFranchiseId } = useAuth();
    const location = useLocation();

    const { selectedMonth, setSelectedMonth } = useAppStore();

    useVersionCheck();

    // Admin Selection State
    const [targetFranchiseId, setTargetFranchiseId] = React.useState<string | null>(null);
    const [targetFranchiseName, setTargetFranchiseName] = React.useState<string | null>(null);

    const { exportCSV } = useExport();

    const isFranchise = roleConfig?.role === 'franchise' || !!impersonatedFranchiseId;
    const canShowInstallPrompt = Boolean(user) && location.pathname !== '/login';

    // --- DATA FETCHING IDENTITY ---
    const profileFranchiseId = roleConfig?.franchiseId;
    const profileName = roleConfig?.name;
    const userUid = user?.uid;

    const dataHookFranchiseId = ((isAdmin && targetFranchiseId)
        ? targetFranchiseId
        : (profileFranchiseId || profileName || userUid || null)) as string | null;

    // Only fetch finance data for admin/franchise — riders never use it
    const isRiderRole = roleConfig?.role === 'rider';
    const {
        rawData: currentData,
        accounting,
        updateFinance: handleUpdate,
        isSaving: saving,
        analysis
    } = useFranchiseFinance({
        franchiseId: user && !isRiderRole ? (dataHookFranchiseId || undefined) : undefined,
        month: selectedMonth
    });

    const report = accounting?.report;

    const handleAdminSelectFranchise = React.useCallback((id: string, name: string) => {
        if (import.meta.env.DEV) console.log(`[Admin] Selecting Franchise: ${name} (${id})`);
        setTargetFranchiseId(id);
        setTargetFranchiseName(name);
    }, []);

    // --- LAYOUT PROPS ---
    const layoutProps = React.useMemo(() => ({
        user, isAdmin, isFranchise,
        viewMode: 'dashboard' as const, setViewMode: () => { },
        franchiseView: 'cockpit' as const, setFranchiseView: () => { },
        targetFranchiseName,
        onLogout: logout,
        onExport: () => exportCSV(report as import('./hooks/useExport').ReportData, selectedMonth, isAdmin && targetFranchiseName ? targetFranchiseName : 'REPAART'),
        sidebarData: currentData, onCalculate: handleUpdate, readOnly: false,
        saving,
        chatData: { report },
        isRider: roleConfig?.role === 'rider'
    }), [user, isAdmin, isFranchise, targetFranchiseName, logout, exportCSV, report, selectedMonth, currentData, handleUpdate, saving, roleConfig?.role]);

    const outletContext = React.useMemo(() => ({
        user,
        franchiseId: dataHookFranchiseId,
        currentData, report, analysis,
        selectedMonth, setSelectedMonth,
        handleAdminSelectFranchise,
        targetFranchiseId,
        setIsSidebarOpen: (_isOpen: boolean) => { }
    }), [user, dataHookFranchiseId, currentData, report, analysis, selectedMonth, setSelectedMonth, handleAdminSelectFranchise, targetFranchiseId]);

    if (authLoading) {
        return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><DashboardSkeleton /></div>;
    }

    return (
        <>
            <ErrorBoundaryWithToast level="page">
                <Suspense fallback={<div className="min-h-screen bg-slate-950 flex items-center justify-center"><DashboardSkeleton /></div>}>
                    <Routes>
                        {/* PUBLIC */}
                        <Route path="/login" element={
                            !user ? <Login /> : (
                                ['rider'].includes(user.role || '')
                                    ? <Navigate to="/rider/dashboard" replace />
                                    : <Navigate to="/dashboard" replace />
                            )
                        } />

                        {/* PROTECTED LAYOUT */}
                        <Route path="/" element={
                            <ProtectedRoute>
                                {['rider'].includes(user?.role || '') ? (
                                    <Navigate to="/rider/dashboard" replace />
                                ) : (
                                    <DashboardLayout {...layoutProps} outletContext={outletContext} />
                                )}
                            </ProtectedRoute>
                        }>
                            {SharedRoutes({
                                user,
                                // Un admin sin flota seleccionada ve todos los riders;
                                // el resto solo los de su propia flota.
                                franchiseId: (isAdmin && !targetFranchiseId)
                                    ? undefined
                                    : (dataHookFranchiseId ?? undefined)
                            })}
                            {AdminRoutes({ targetFranchiseId })}
                            <Route path="*" element={<NotFound />} />
                        </Route>

                        {/* RIDER APP */}
                        {RiderRoutes()}
                    </Routes>
                </Suspense>

                {canShowInstallPrompt && <InstallPrompt />}
            </ErrorBoundaryWithToast>

            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#1e293b',
                        color: '#f1f5f9',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '500',
                        padding: '12px 16px',
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3)',
                    },
                    success: {
                        iconTheme: { primary: '#10b981', secondary: '#f1f5f9' },
                    },
                    error: {
                        iconTheme: { primary: '#ef4444', secondary: '#f1f5f9' },
                        duration: 5000,
                    },
                }}
            />

            {import.meta.env.DEV && <DebugFirestore />}
        </>
    );
}

export default App;
